/**
 * Gap 3 Path C — public "request access" form.
 *
 * No auth required. Anyone who lands here and submits the form creates an
 * access_requests row. Xamun staff approve in /admin/access-requests,
 * which mints an invitation code that gets emailed to the requester so
 * they can follow the Path A flow on /post-login.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import type { ApiError} from "../api/client";
import { apiPost } from "../api/client";

export function RequestAccessPage() {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [company, setCompany] = useState("");
  const [useCase, setUseCase] = useState("");

  const submit = useMutation<
    { id: string; status: string; message: string },
    ApiError,
    void
  >({
    mutationFn: () =>
      apiPost("/api/onboarding/request-access", {
        email: email.trim(),
        ...(displayName.trim() && { display_name: displayName.trim() }),
        ...(company.trim() && { company: company.trim() }),
        ...(useCase.trim() && { use_case: useCase.trim() }),
      }),
  });

  if (submit.isSuccess) {
    return (
      <main className="min-h-screen bg-bg-canvas p-8 font-geist">
        <div className="mx-auto max-w-2xl">
          <header className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-text-3">
              PlayStudio · Request access
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text">
              We got it
            </h1>
          </header>
          <div className="rounded-lg border border-border p-6 text-sm text-text-2">
            {submit.data.message}
          </div>
          <p className="mt-4 text-sm text-text-3">
            Have a code already? <Link to="/post-login" className="text-accent underline">Sign in</Link>.
          </p>
        </div>
      </main>
    );
  }

  const canSubmit = email.trim().length > 3 && !submit.isPending;

  return (
    <main className="min-h-screen bg-bg-canvas p-8 font-geist">
      <div className="mx-auto max-w-2xl">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-text-3">
            PlayStudio
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text">
            Request access
          </h1>
          <p className="mt-2 text-sm text-text-2">
            PlayStudio is in invite-only testing. Tell us a bit about you and
            we'll get back with an invitation code.
          </p>
        </header>

        <section className="rounded-lg border border-border p-6">
          <label className="block text-sm">
            <span className="font-medium">Email</span>
            <input
              className="mt-2 w-full rounded-md border border-border bg-bg-soft p-2 text-sm"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </label>

          <label className="mt-3 block text-sm">
            <span className="font-medium">Your name</span>{" "}
            <span className="text-text-3">(optional)</span>
            <input
              className="mt-2 w-full rounded-md border border-border bg-bg-soft p-2 text-sm"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </label>

          <label className="mt-3 block text-sm">
            <span className="font-medium">Company</span>{" "}
            <span className="text-text-3">(optional)</span>
            <input
              className="mt-2 w-full rounded-md border border-border bg-bg-soft p-2 text-sm"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </label>

          <label className="mt-3 block text-sm">
            <span className="font-medium">What are you hoping to use it for?</span>{" "}
            <span className="text-text-3">(optional)</span>
            <textarea
              className="mt-2 w-full rounded-md border border-border bg-bg-soft p-2 text-sm"
              rows={4}
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
            />
          </label>

          {submit.error && (
            <div className="mt-4 rounded-md bg-danger-soft p-3 text-sm text-danger">
              Could not submit ({submit.error.status}). Try again or email us.
            </div>
          )}

          <button
            type="button"
            className="mt-4 rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={!canSubmit}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? "Submitting…" : "Request access"}
          </button>
        </section>

        <p className="mt-6 text-sm text-text-3">
          Already have a code?{" "}
          <Link to="/post-login" className="text-accent underline">
            Sign in
          </Link>
          .
        </p>
      </div>
    </main>
  );
}
